import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { MenuTrigger } from 'react-aria-components';
import useEmblaCarousel from 'embla-carousel-react';

import { useI18n } from 'context/i18n';
import { getDomainValue, isDomain } from 'utils/domain';
import type { TRecommendedProduct, TSize } from 'types/product';
import { ProgressiveImg } from 'components/base/image';
import { Price } from 'components/product/price';
import { useProductLive } from 'actions/products';
import { Button } from 'components/ui/button';
import { MAX_BAG_LENGTH, TARGET_SIZE_LABEL_MARKETS } from 'utils/constants';
import { nnormalSizesMap } from 'utils/helpers';
import { dataLayerCartClickV2, dataLayerHandleEvent } from 'utils/dataLayers';
import { useIsMounted, useLocale } from 'hooks';
import { handleAddToBag } from 'context/bag';
import { setBagCookies } from 'utils/bag';
import { MenuItem, Menu } from 'components/ui/menu';
import { parseProductsEcommerceGA4 } from 'utils/gtmUtils';

type RecommendedItemProps = {
  item: TRecommendedProduct;
  onError: (error: string) => void;
  onSuccess: () => void;
};

function RecommendedItem({ item, onError, onSuccess }: RecommendedItemProps) {
  const { t, bagLength, profileData, setBag, setBagLength, setRecommendedProductsForBag, setPromoLabel, setPromoLabelType } = useI18n();
  const { locale, country } = useLocale();
  const { data: live, status } = useProductLive({ sku: item.id, target: item.targetId });

  const sizes = useMemo<TSize[]>(() => {
    let parsedTarget = 'M';
    if (country === 'US') {
      parsedTarget = 'US';
    } else if (['M', 'W'].includes(item.targetId)) {
      parsedTarget = item.targetId;
    }
    const sizesList =
      (live?.productSheetLive?.sizes ?? []).map((s: TSize) => {
        let sizeLabel = s.label;
        if (isDomain('camper') && TARGET_SIZE_LABEL_MARKETS.includes(country)) {
          sizeLabel = `${s.label} ${country} / ${s.value} EU`;
        }
        if (isDomain('nnormal')) {
          sizeLabel = (nnormalSizesMap as any)?.[parsedTarget]?.[s.value] ?? s.label;
        }
        return {
          ...s,
          label: sizeLabel,
        } as TSize;
      }) ?? [];
    return sizesList;
  }, [country, item.targetId, live?.productSheetLive?.sizes]);

  const allUnavailable = sizes.every((size: TSize) => !size.available);
  const isUniqueSize = sizes.length === 1 && sizes[0]?.value === '';

  const isDisabled = status === 'pending' || sizes.length === 0 || bagLength >= MAX_BAG_LENGTH || allUnavailable;

  const add = useMutation({
    mutationFn: async ({ size }: { size: TSize | undefined }) => {
      if (!size) throw new Error('Size is required');
      return await handleAddToBag({
        mainId: item.id,
        payload: {
          products: [
            {
              id: item.id,
              size: size.value,
              thumbnail: item.imageBag,
            },
          ],
        },
        locale,
      });
    },
    onSuccess: ({ cartResult, crossResult }: any) => {
      let updatedPromoLabel = null;
      let updatedPromoLabelType = null;

      if (cartResult.hashOrderId) {
        dataLayerCartClickV2({ data: cartResult.shoppingCart, addedIds: [item.id] as TODO, bagId: cartResult.hashOrderId, currency: profileData?.currencySap });
        setBagCookies({ hash: encodeURI(cartResult.hashOrderId), market: country });

        const { shoppingCart } = cartResult;
        setBag(shoppingCart);
        setBagLength(shoppingCart.length);

        (setRecommendedProductsForBag as any)((prev: TRecommendedProduct[]) =>
          [...crossResult, ...prev].map((item: TRecommendedProduct, i: number) => ({ ...item, order: i })),
        );

        if (profileData?.config?.show_free_shipping === true) {
          updatedPromoLabel = cartResult?.freeShippingLabel || null;
        }
        // let promoLabelObtained = false;
        if (profileData?.config?.show_gift_purchase === true) {
          updatedPromoLabel = cartResult?.gwplabel || cartResult?.freeShippingLabel || null;
          if (cartResult?.gwplabelObtained && cartResult?.gwplabelObtained !== '') {
            updatedPromoLabel = cartResult?.gwplabelObtained;
            // promoLabelObtained = true;
            updatedPromoLabelType = 'obtained';
          }
        }
        setPromoLabel(updatedPromoLabel ?? '');
        setPromoLabelType((updatedPromoLabelType ?? '') as any);
      }
      onSuccess();
    },
    onError: (error) => {
      onError(error.message);
    },
  });

  return (
    <div className="flex h-full w-full flex-grow select-none flex-col gap-4 overflow-hidden">
      {/* Image */}
      <a href={item.url} className="block w-full">
        <ProgressiveImg
          src={item.images.xs.main}
          pictureClassName="block  aspect-square lab:aspect-auto w-full"
          className="aspect-square w-full object-cover lab:aspect-auto"
        />
      </a>
      {/* Name */}
      <div className="mt-auto flex flex-grow flex-col gap-1.5">
        <div>
          <p className="mb-0 text-sm nnormal:font-secondary nnormal:font-bold lab:text-xs lab:font-bold lab:uppercase">{item.name}</p>
          {/* Description */}
          <p className="mb-0 text-copy empty:hidden nnormal:my-[7px] nnormal:text-sm camper:mb-2.5 camper:hidden camper:text-[0.6em] camper:text-[var(--color-border-panel)] lab:hidden lab:text-xs lab:font-bold lab:uppercase">
            {item.conceptName}
          </p>
        </div>
        {/* Price */}
        <Price price={item.prices} className="mt-auto nnormal:text-xs" />
        {/* Add to bag button */}
        {sizes.length > 0 ?
          <div>
            {isUniqueSize ?
              <Button
                isPending={add.isPending}
                variant={getDomainValue({ camper: 'outline', nnormal: 'outline', camperlab: 'none' })}
                isDisabled={isDisabled}
                className="h-8 min-w-[80%] px-2 text-xs lab:px-0 lab:text-sm lab:uppercase lab:lg:text-xs lab:[&>span]:underline"
                onPress={() => add.mutate({ size: sizes[0] })}
              >
                {t('ficha.producto', 'add.to.bag')}
              </Button>
            : <MenuTrigger>
                <Button
                  variant={getDomainValue({ camper: 'outline', nnormal: 'outline', camperlab: 'none' })}
                  isDisabled={isDisabled}
                  className="relative h-8 min-w-[80%] px-2 text-xs lab:px-0 lab:text-sm lab:uppercase lab:lg:text-xs lab:[&>span]:underline"
                  isPending={add.isPending}
                >
                  {t('ficha.producto', 'add.to.bag')}
                </Button>
                <Menu placement="top" data-element="recommended-bag-menu">
                  {sizes.map((size) => {
                    return (
                      <MenuItem isDisabled={!size.available} key={size.value} textValue={size.value} className="text-sm" onAction={() => add.mutate({ size })}>
                        {size.value}
                      </MenuItem>
                    );
                  })}
                </Menu>
              </MenuTrigger>
            }
          </div>
        : null}
      </div>
    </div>
  );
}

const currentList = 'Add_to_bag_may_also_be_interested';

const basis = getDomainValue({
  camper:
    'basis-[45%] [@media(min-width:400px)]:basis-[31%] [@media(min-width:550px)]:basis-[24%] [@media(min-width:670px)]:basis-[21%] [@media(min-width:768px)]:basis-[45%]',
  nnormal: 'camper',
  camperlab: 'basis-[28%] sm:basis-[18%] md:basis-[14%] lg:basis-[28%] ',
});

export function RecommendedBag() {
  const { t, recommendedProductsForBag, profileData } = useI18n();
  const isMobile = useRef(false);
  const isMounted = useIsMounted();
  const [error, setError] = useState<string | null>(null);
  const initDataLayer = useRef(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, dragFree: true });

  const handleError = (error: string) => {
    setError(error);
    setTimeout(() => {
      if (isMounted()) {
        setError(null);
      }
    }, 3000);
  };

  useEffect(() => {
    isMobile.current = window.innerWidth <= 500;
  }, []);

  // quitamos los duplicados y ordenamos por la propiedad order
  const recommendedForDisplay: TRecommendedProduct[] = (recommendedProductsForBag as TRecommendedProduct[])
    .reduce((acc: TRecommendedProduct[], current: TRecommendedProduct) => {
      if (!acc.some((item) => item.id === current.id)) {
        acc.push({
          ...current,
          images: {
            full: { main: current.imageBag },
            lg: { main: current.imageBag },
            md: { main: current.imageBag },
            xs: { main: current.imageBag },
          },
        } as TRecommendedProduct);
      }
      return acc;
    }, [] as TRecommendedProduct[])
    .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

  const [firstProduct] = recommendedForDisplay;
  const recommendedTitle = (firstProduct as any)?.sunnei ? t('ficha.producto', 'completa.sunnei') : t('ficha.producto', 'tambien.puede.interesarte');

  useEffect(() => {
    // metemos un view_item_list siempre que se monte products row
    try {
      if (recommendedForDisplay.length > 0 && initDataLayer.current === false) {
        const eventLayerGA4 = {
          event: 'view_item_list',
          nonInteraction: '1',
          ecommerce: {
            currency: profileData?.currencySap ?? 'EUR',
            items: parseProductsEcommerceGA4(recommendedForDisplay, currentList, currentList, 0),
          },
        };

        dataLayerHandleEvent(eventLayerGA4);
        initDataLayer.current = true;
      }
    } catch (e) {
      console.error(e);
    }
  }, [recommendedForDisplay, profileData?.currencySap]);

  if (!Array.isArray(recommendedProductsForBag) || recommendedProductsForBag.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <h3
        className={`mb-3 mr-4 flex border-b border-b-muted pb-1 text-xs uppercase lab:mb-0 lab:border-none lab:pl-2.5 lab:text-sm lab:lg:text-xs`}
        data-trackingga4={`${currentList?.toLowerCase()}|slider`}
      >
        {recommendedTitle}
      </h3>

      <div className="overflow-hidden pr-4 lab:pb-2.5 lab:pr-0">
        <div className="relative w-full overflow-hidden">
          <div className="relative" ref={emblaRef}>
            <div className="-ml-1 flex lab:ml-[5px] lab:mr-[15px]">
              {recommendedForDisplay.map((item) => (
                <div key={item.id} className={`min-w-0 shrink-0 grow-0 px-[5px] ${basis}`}>
                  <RecommendedItem item={item} onError={handleError} onSuccess={() => emblaApi?.scrollTo(0)} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <p className="mb-0 mt-2 text-xs text-error empty:hidden">{error ?? ''}</p>
    </div>
  );
}
